import { neon } from '@neondatabase/serverless';

// Remove channel_binding param which is incompatible with the neon HTTP driver
let DATABASE_URL = import.meta.env.VITE_DATABASE_URL || '';
DATABASE_URL = DATABASE_URL.replace(/[&?]channel_binding=require/g, '');

const sql = neon(DATABASE_URL, {
  fetchOptions: { cache: 'no-store' }
});

// Stable IDs that match the mobile app's `prefix_<uuid>` convention so app and
// web write to the same primary-key namespace.
const rid = (prefix) =>
  `${prefix}_${crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2)}`;

// Find-or-create a phone-based mobile user for an order's customer. Mobile's
// orders table requires a `customer_id` referencing users; the web only has a
// phone + name. We dedupe by phone so a returning customer keeps the same id.
async function getOrCreateCustomer(phone, name) {
  if (!phone) throw new Error('customer_phone is required to place an order');
  const existing = await sql`
    SELECT id, name FROM users WHERE phone = ${phone} AND role = 'customer' LIMIT 1
  `;
  if (existing[0]) return existing[0];
  const created = await sql`
    INSERT INTO users (id, name, phone, role)
    VALUES (${rid('user')}, ${name || 'Guest'}, ${phone}, 'customer')
    RETURNING id, name
  `;
  return created[0];
}

export const db = {
  // ── Restaurants ──────────────────────────────────────────────────────────
  // Aliases `is_open AS is_active` so the existing AdminPanel UI keeps working
  // without any component changes.

  async getRestaurants() {
    return await sql`
      SELECT *, is_open AS is_active
      FROM restaurants
      ORDER BY name
    `;
  },

  async createRestaurant(data) {
    const result = await sql`
      INSERT INTO restaurants (
        id, name, description, cuisine_type, address, phone,
        image_url, logo_url, delivery_time, delivery_fee,
        minimum_order, is_open
      )
      VALUES (
        ${rid('rest')}, ${data.name},
        ${data.description || null}, ${data.cuisine_type || null},
        ${data.address || null}, ${data.phone || null},
        ${data.image_url || null}, ${data.logo_url || null},
        ${data.delivery_time || null}, ${data.delivery_fee || null},
        ${data.minimum_order || null},
        ${data.is_active !== undefined ? data.is_active : true}
      )
      RETURNING *, is_open AS is_active
    `;
    return result[0];
  },

  async updateRestaurant(id, data) {
    const result = await sql`
      UPDATE restaurants
      SET
        name = ${data.name},
        description = ${data.description || null},
        cuisine_type = ${data.cuisine_type || null},
        is_open = ${data.is_active !== undefined ? data.is_active : true},
        updated_at = NOW()
      WHERE id = ${id}
      RETURNING *, is_open AS is_active
    `;
    return result[0];
  },

  async deleteRestaurant(id) {
    await sql`DELETE FROM restaurants WHERE id = ${id}`;
  },

  // ── Menu items ───────────────────────────────────────────────────────────

  async getMenuItems(restaurantId) {
    return await sql`
      SELECT
        mi.*,
        mc.name AS category_name,
        json_build_object('id', mc.id, 'name', mc.name) AS menu_categories
      FROM menu_items mi
      LEFT JOIN menu_categories mc ON mi.category_id = mc.id
      WHERE mi.restaurant_id = ${restaurantId}
      ORDER BY mi.name
    `;
  },

  async createMenuItem(data) {
    const result = await sql`
      INSERT INTO menu_items (
        id, restaurant_id, category_id, name, description,
        price, image_url, is_available
      )
      VALUES (
        ${rid('menu')}, ${data.restaurant_id}, ${data.category_id || null},
        ${data.name}, ${data.description || ''}, ${data.price},
        ${data.image_url || null},
        ${data.is_available !== undefined ? data.is_available : true}
      )
      RETURNING *
    `;
    return result[0];
  },

  async updateMenuItem(id, data) {
    const result = await sql`
      UPDATE menu_items
      SET
        name = ${data.name},
        description = ${data.description || ''},
        price = ${data.price},
        category_id = ${data.category_id || null},
        is_available = ${data.is_available !== undefined ? data.is_available : true},
        updated_at = NOW()
      WHERE id = ${id}
      RETURNING *
    `;
    return result[0];
  },

  async deleteMenuItem(id) {
    await sql`DELETE FROM menu_items WHERE id = ${id}`;
  },

  // ── Menu categories ──────────────────────────────────────────────────────

  async getMenuCategories(restaurantId) {
    return await sql`
      SELECT * FROM menu_categories
      WHERE restaurant_id = ${restaurantId}
      ORDER BY name
    `;
  },

  async createMenuCategory(data) {
    const result = await sql`
      INSERT INTO menu_categories (id, restaurant_id, name, is_active)
      VALUES (
        ${rid('mcat')}, ${data.restaurant_id}, ${data.name},
        ${data.is_active !== undefined ? data.is_active : true}
      )
      RETURNING *
    `;
    return result[0];
  },

  async toggleItemAvailability(id, isAvailable) {
    const result = await sql`
      UPDATE menu_items
      SET is_available = ${isAvailable}, updated_at = NOW()
      WHERE id = ${id}
      RETURNING *
    `;
    return result[0];
  },

  async getActiveRestaurantsWithMenus() {
    const restaurants = await sql`
      SELECT *, is_open AS is_active
      FROM restaurants
      WHERE is_open = true
      ORDER BY name
    `;

    const restaurantsWithItems = await Promise.all(
      restaurants.map(async (restaurant) => {
        const items = await sql`
          SELECT
            mi.*,
            mc.name AS category_name
          FROM menu_items mi
          LEFT JOIN menu_categories mc ON mi.category_id = mc.id
          WHERE mi.restaurant_id = ${restaurant.id} AND mi.is_available = true
          ORDER BY mi.is_featured DESC NULLS LAST, mi.name
        `;
        return { ...restaurant, items };
      })
    );

    return restaurantsWithItems;
  },

  // ── Delivery zones ───────────────────────────────────────────────────────
  // Now sourced from the admin-managed service_locations table (the same one
  // the mobile app uses), so the two systems agree on coverage.

  async getDeliveryAreas() {
    const result = await sql`
      SELECT name FROM service_locations
      WHERE is_active = true
      ORDER BY display_order, name
    `;
    return result.map((r) => r.name);
  },

  // ── Partnership applications ─────────────────────────────────────────────

  async submitPartnerApplication(data) {
    const result = await sql`
      INSERT INTO partnership_applications (
        business_name, owner_name, email, phone,
        address, business_type, description
      )
      VALUES (
        ${data.businessName}, ${data.ownerName}, ${data.email},
        ${data.phone}, ${data.address}, ${data.businessType},
        ${data.description}
      )
      RETURNING *
    `;
    return result[0];
  },

  // ── Web-side customer identity (email + password). Independent of mobile's
  // phone-based users table.

  async createCustomerProfile(data) {
    const result = await sql`
      INSERT INTO customer_profiles (
        name, email, phone, address, password_hash
      )
      VALUES (
        ${data.name}, ${data.email}, ${data.phone || null},
        ${data.address || null}, ${data.password_hash}
      )
      RETURNING id, name, email, phone, address, created_at, updated_at
    `;
    return result[0];
  },

  async loginCustomer(email, passwordHash) {
    const result = await sql`
      SELECT id, name, email, phone, address, created_at, updated_at
      FROM customer_profiles
      WHERE email = ${email} AND password_hash = ${passwordHash}
    `;
    return result[0] || null;
  },

  async getCustomerProfile(email) {
    const result = await sql`
      SELECT id, name, email, phone, address, created_at, updated_at
      FROM customer_profiles WHERE email = ${email}
    `;
    return result[0] || null;
  },

  async updateCustomerProfile(id, data) {
    const result = await sql`
      UPDATE customer_profiles
      SET
        name = ${data.name},
        phone = ${data.phone || null},
        address = ${data.address || null},
        updated_at = NOW()
      WHERE id = ${id}
      RETURNING id, name, email, phone, address, created_at, updated_at
    `;
    return result[0];
  },

  async changeCustomerPassword(id, oldPasswordHash, newPasswordHash) {
    const result = await sql`
      UPDATE customer_profiles
      SET password_hash = ${newPasswordHash}, updated_at = NOW()
      WHERE id = ${id} AND password_hash = ${oldPasswordHash}
      RETURNING id
    `;
    return result[0] || null;
  },

  // ── Orders ───────────────────────────────────────────────────────────────
  // Writes to the unified `orders` + `order_items` tables shared with the
  // mobile app. Snapshots restaurant name and the customer's mobile user_id
  // (created on-demand from the phone) so the mobile app can show this order
  // to the matching logged-in user.

  async createOrder(orderData) {
    // Ensure a mobile-side user exists for this phone so we can satisfy the
    // orders.customer_id NOT NULL constraint and link the order to the app.
    const customer = await getOrCreateCustomer(orderData.customer_phone, orderData.customer_name);

    const restaurantRow = await sql`
      SELECT name FROM restaurants WHERE id = ${orderData.restaurant_id} LIMIT 1
    `;
    const restaurantName = restaurantRow[0]?.name || 'Restaurant';

    const result = await sql`
      INSERT INTO orders (
        id, customer_id, customer_name, customer_phone,
        restaurant_id, restaurant_name,
        payment_method,
        delivery_address_label,
        status, subtotal, delivery_fee, total,
        customer_email, special_instructions
      )
      VALUES (
        ${rid('ord')}, ${customer.id}, ${orderData.customer_name}, ${orderData.customer_phone},
        ${orderData.restaurant_id}, ${restaurantName},
        'cash_on_delivery',
        ${orderData.delivery_address || 'Unknown'},
        'received', ${orderData.subtotal},
        ${orderData.delivery_fee || 0}, ${orderData.total},
        ${orderData.customer_email || null}, ${orderData.special_instructions || null}
      )
      RETURNING *
    `;
    return result[0];
  },

  async createOrderItems(orderId, items) {
    for (const item of items) {
      const itemRow = await sql`
        SELECT mi.name, mi.restaurant_id, r.name AS restaurant_name
        FROM menu_items mi
        LEFT JOIN restaurants r ON r.id = mi.restaurant_id
        WHERE mi.id = ${item.menu_item_id}
        LIMIT 1
      `;
      const name = itemRow[0]?.name || 'Item';
      const restaurantId = itemRow[0]?.restaurant_id || null;
      const restaurantName = itemRow[0]?.restaurant_name || null;
      await sql`
        INSERT INTO order_items (
          id, order_id, menu_item_id, name, quantity, unit_price, line_total,
          customizations, restaurant_id, restaurant_name
        )
        VALUES (
          ${rid('oi')}, ${orderId}, ${item.menu_item_id}, ${name},
          ${item.quantity}, ${item.unit_price}, ${item.subtotal},
          ${item.customizations || null}, ${restaurantId}, ${restaurantName}
        )
      `;
    }
  }
};
